import { faChevronsDown } from "@fortawesome/pro-regular-svg-icons/faChevronsDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Rating, Stack, Typography, useTheme } from "@mui/material";
import { MprcLink, MprcText } from "components";
import { AssessmentAlternativeRevisionDto, AssessmentInstanceDto, AssessmentPlayerMode, AssessmentQuestionDto } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AssPlayerTotalAnswers } from "views/assessment/player/Question/AssPlayerTotalAnswers";
import { UsersAnswerDrawer } from "views/assessment/player/common/UsersAnswerDrawer";

interface IProps {
    viewData: AssessmentInstanceDto;
    question: AssessmentQuestionDto;
    deployId?: string;
    rating: number;
    onChange: (rating: number) => void;
}

export const AssPlayerStar: React.FC<IProps> = ({ viewData, question, deployId, rating, onChange }) => {

    const { t } = useTranslation("assessment");

    const theme = useTheme();

    const [dialog, setDialog] = useState<any>();

    ///
    const showAlternativeAnswers = (alternative: AssessmentAlternativeRevisionDto) => {
        setDialog(
            <UsersAnswerDrawer
                deployId={deployId!}
                instanceId={viewData.instanceId!}
                questionId={question.questionId}
                version={question.version}
                alternativeId={alternative.alternativeId}
                title={t("AssPlayer.Question.StarDrawerTitle")}
                onClose={() => setDialog(null)}
            />
        );
    };

    ///
    const RenderAvailable = () => {
        const readOnly = viewData.playerMode == AssessmentPlayerMode.UserReview;
        return (
            <Rating
                value={rating}
                size="large"
                readOnly={readOnly}
                onChange={(event, newValue) => {
                    onChange(newValue ?? 0);
                }}
            />
        );
    };

    ///
    const RenderRevision = () => {
        return (
            <>
                <Stack direction="row" spacing={2} alignItems="center" marginBottom={2}>
                    <Rating
                        value={question.revision?.averagePoints ?? 0}
                        size="large"
                        precision={0.1}
                        readOnly
                    />
                    <Typography variant="body1" color={theme.palette.muted.main}>
                        <>{t("AssPlayer.Question.Revision.AverageText")}:&nbsp;</>
                        <MprcText color={question.revision?.averagePoints ?? 0 > 3.5 ? "success" : "error"}>
                            <strong>{Formaters.number(question.revision?.averagePoints ?? 0, 1)}%</strong>
                        </MprcText>
                    </Typography>
                </Stack>

                <Stack direction="column">
                    <Typography variant="caption">
                        {t("AssPlayer.Question.Revision.YouSelectedText", { name: Formaters.number(rating ?? 0, 1) })}
                    </Typography>
                    <AssPlayerTotalAnswers
                        deployId={deployId}
                        viewData={viewData}
                        question={question}
                    />
                </Stack>

                <Accordion
                    sx={{
                        border: `1px solid ${theme.palette.muted.main}`,
                        boxShadow: "none",
                        marginTop: theme.spacing(2),
                    }}
                >
                    <AccordionSummary
                        expandIcon={<FontAwesomeIcon icon={faChevronsDown} size="2x" color={theme.palette.muted.light} />}
                        aria-controls="panel1-distribution"
                        id="panel1-distribution"
                    >
                        <Typography variant="body1" color={theme.palette.muted.main} textTransform="uppercase">
                            {t("AssPlayer.Question.Revision.AnswerDistributionText")}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {
                            question.revision?.alternatives.map((item, index) => {
                                return (
                                    <Grid key={item.alternativeId} container alignContent="center" alignItems="flex-start" spacing={2}>
                                        <Grid>
                                            <Rating
                                                name="read-only"
                                                value={5 - index}
                                                readOnly
                                            />
                                        </Grid>
                                        {
                                            item?.totalAnswers != undefined && viewData.surv?.identifyUsers == true &&
                                            <Grid>
                                                {
                                                    question.revision?.identifyAnswers
                                                        ?
                                                        <MprcLink onClick={() => showAlternativeAnswers(item)}>
                                                            <Stack><MprcText color="highlight">({item.totalAnswers})</MprcText></Stack>
                                                        </MprcLink>
                                                        :
                                                        <MprcText color="muted">({item.totalAnswers})</MprcText>
                                                }
                                            </Grid>
                                        }
                                    </Grid>
                                );
                            })
                        }
                    </AccordionDetails>
                </Accordion>
            </>
        );
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {dialog}

            <Box>
                <>
                    {
                        viewData.playerMode == AssessmentPlayerMode.FinalResult
                            ? RenderRevision()
                            : RenderAvailable()
                    }
                </>
            </Box>
        </>
    );
};
